/* La ficha que se adapta, sin pantalla · CATÁLOGO VIVO F2.
   Funciones puras que deciden qué se pinta, qué se valida y qué se manda.

   LA MEMORIA DE LA PANTALLA. Lo tecleado vive en un solo mapa por clave
   (`{ resistencia: '10k', marca: 'Steren' }`), no por categoría. Cambiar de
   categoría solo cambia QUÉ claves se pintan: lo que también existe en la
   nueva se ve igual, y lo que no se queda en el mapa y vuelve si se regresa
   a la categoría anterior. Nunca un «se perderán tus cambios» (F2.2). */
import { formatear, interpretar, UnidadNoValida } from './unidades';

const vacio = (v) => v == null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0);

// El valor que se pinta en un campo: lo tecleado, si no lo guardado, si no la
// omisión de la plantilla (marcada como sugerida para que se vea que nadie lo
// escribió).
export function valorDe(atributo, memoria) {
  if (Object.prototype.hasOwnProperty.call(memoria, atributo.clave)) return memoria[atributo.clave];
  if (!vacio(atributo.valor)) return atributo.valor;
  return atributo.valor_por_omision ?? '';
}

// De dónde salió lo que se ve (F2.5): la marca por campo.
export function origenDe(atributo, memoria, origenes = {}) {
  if (Object.prototype.hasOwnProperty.call(memoria, atributo.clave)) {
    return origenes[atributo.clave] || 'tuyo';
  }
  if (!vacio(atributo.valor)) return origenes[atributo.clave] || 'guardado';
  if (!vacio(atributo.valor_por_omision)) return 'sugerido';
  return null;
}

export const TEXTO_ORIGEN = {
  tuyo: 'escrito por ti',
  guardado: 'ya estaba guardado',
  sugerido: 'sugerido por la categoría',
};

// Validación en vivo (F2.4). Devuelve { valido, mensaje, muestra }.
// `muestra` es cómo quedará («10 kΩ»), para que quien escribe «10k» vea que
// se entendió.
export function validarEnVivo(atributo, valor) {
  const nombre = atributo.nombre || atributo.etiqueta;
  if (vacio(valor)) {
    return atributo.obligatorio
      ? { valido: false, mensaje: `«${nombre}» es obligatorio en esta categoría.`, faltaObligatorio: true }
      : { valido: true };
  }
  const reglas = atributo.validacion || {};
  const rango = (n) => {
    if (reglas.min != null && n < Number(reglas.min)) return `«${nombre}» no puede ser menor que ${reglas.min}.`;
    if (reglas.max != null && n > Number(reglas.max)) return `«${nombre}» no puede ser mayor que ${reglas.max}.`;
    return null;
  };
  const texto = String(valor).trim();
  switch (atributo.tipo) {
    case 'NUMERO': {
      try {
        const n = interpretar(texto, atributo.unidad_base || '', atributo.unidades || []);
        const fuera = rango(n);
        if (fuera) return { valido: false, mensaje: fuera };
        return { valido: true, muestra: formatear(n, atributo.unidad_base || ''), base: n };
      } catch (e) {
        if (e instanceof UnidadNoValida) return { valido: false, mensaje: `«${nombre}»: ${e.message}` };
        throw e;
      }
    }
    case 'ENTERO': {
      const t = texto.replace(/,/g, '');
      if (!/^[-+]?[0-9]+$/.test(t)) return { valido: false, mensaje: `«${nombre}» es un número entero, sin decimales: 12, 300.` };
      const fuera = rango(Number(t));
      return fuera ? { valido: false, mensaje: fuera } : { valido: true };
    }
    case 'LISTA': {
      const ok = (atributo.opciones || []).some((o) => o.toLowerCase() === texto.toLowerCase());
      return ok ? { valido: true } : { valido: false, mensaje: `Elige una opción de «${nombre}».` };
    }
    case 'FECHA':
      return /^\d{4}-\d{2}-\d{2}$/.test(texto) ? { valido: true }
        : { valido: false, mensaje: `«${nombre}» es una fecha; elígela en el calendario.` };
    case 'TEXTO':
      if (texto.length > 500) return { valido: false, mensaje: `«${nombre}» admite hasta 500 letras.` };
      if (reglas.regex && !new RegExp(reglas.regex).test(texto)) {
        return { valido: false, mensaje: `«${nombre}» no tiene la forma esperada.${atributo.ayuda ? ` ${atributo.ayuda}` : ''}` };
      }
      return { valido: true };
    default:
      return { valido: true };
  }
}

// Lo que impide guardar: SOLO los obligatorios que faltan (F2.4). Un dato
// opcional mal escrito se marca en su campo y no se manda, pero no apaga el
// botón: el resto de la ficha se puede guardar.
export function faltanObligatorios(plantilla, memoria) {
  return (plantilla || []).filter((a) => a.obligatorio && vacio(valorDe(a, memoria)))
    .map((a) => a.nombre || a.etiqueta);
}

// Lo que se manda al servidor: lo que la persona tocó (o la omisión sugerida
// que aceptó al dejarla), de los datos de ESTA categoría, y que es válido.
// Lo tecleado para otra categoría se queda en la memoria y no viaja.
export function paraGuardar(plantilla, memoria) {
  const salida = {};
  for (const a of plantilla || []) {
    const tocado = Object.prototype.hasOwnProperty.call(memoria, a.clave);
    const sugerido = !tocado && vacio(a.valor) && !vacio(a.valor_por_omision);
    if (!tocado && !sugerido) continue;
    const v = tocado ? memoria[a.clave] : a.valor_por_omision;
    if (tocado && String(v ?? '') === String(a.valor ?? '')) continue;
    if (!vacio(v) && !validarEnVivo(a, v).valido) continue;
    salida[a.clave] = Array.isArray(v) ? v.join(', ') : v;
  }
  return salida;
}

// El teclado del teléfono (F2.6): numérico para números.
export function modoTeclado(atributo) {
  if (atributo.tipo === 'ENTERO') return 'numeric';
  if (atributo.tipo === 'NUMERO') return atributo.unidad_base ? 'text' : 'decimal';
  return 'text';
}

// El buscador de categorías (F2.1): cada palabra en cualquier parte de la
// ruta, sin acentos ni mayúsculas. «electronico resis» → «Componente electrónico > Resistencia».
const normalCategoria = (t) => (t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function filtrarCategorias(categorias, texto) {
  const palabras = normalCategoria(texto).split(/\s+/).filter(Boolean);
  if (!palabras.length) return categorias || [];
  return (categorias || []).filter((c) => {
    const ruta = normalCategoria(c.ruta_texto || c.nombre);
    return palabras.every((p) => ruta.includes(p));
  });
}

